import { useCallback, useMemo } from 'react';
import { CheckCircle, Clock, ShieldCheck } from 'lucide-react';
import { extractFieldsAndSignatures } from '../../utils/editorJsonFields';

function formatSignedAt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function SignatureReceiptFlow({ editorJson, signatures, signatureMeta }) {
  const steps = useMemo(() => {
    if (!editorJson) return [];
    const { signatureBlocks } = extractFieldsAndSignatures(editorJson);
    if (signatureBlocks.length === 0) return [];

    const enriched = signatureBlocks.map((block) => {
      const sig = signatures?.[block.id];
      const meta = signatureMeta?.[block.id];
      const submissionSigned = sig != null && String(sig).trim() !== '';
      const preSigned = !submissionSigned && !!block.defaultSignature;
      const signed = submissionSigned || preSigned;
      const signerName = submissionSigned
        ? (meta?.signerName || '')
        : (preSigned ? (block.defaultSignerName || '') : '');
      const email = submissionSigned
        ? (meta?.signerEmail || block.assigneeEmail || '')
        : (block.assigneeEmail || (preSigned ? block.defaultSignerEmail : '') || '');
      return {
        id: block.id,
        label: block.label || 'Signature',
        email,
        signingOrderIndex: block.signingOrderIndex,
        signed,
        signedAt: submissionSigned ? meta?.signedAt : (preSigned ? block.defaultSignedAt : null) || null,
        signerName,
      };
    });

    return enriched.sort((a, b) => {
      if (a.signed !== b.signed) return a.signed ? -1 : 1;
      const ai = a.signingOrderIndex;
      const bi = b.signingOrderIndex;
      if (ai != null && bi != null) return ai - bi;
      if (ai != null) return -1;
      if (bi != null) return 1;
      return 0;
    });
  }, [editorJson, signatures, signatureMeta]);

  const scrollToBlock = useCallback((blockId) => {
    const el = document.querySelector(`[data-signature-block-id="${blockId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const allSigned = steps.length > 0 && steps.every((s) => s.signed);

  if (steps.length === 0) return null;

  return (
    <div className="mb-4 flex flex-col gap-2">
      {allSigned && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 mb-1">
          <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
          <p className="text-sm font-medium text-emerald-700">All parties have signed</p>
        </div>
      )}
      {steps.map((step) => (
        <div
          key={step.id}
          className={`flex items-center gap-2.5${!step.signed ? ' cursor-pointer' : ''}`}
          onClick={!step.signed ? () => scrollToBlock(step.id) : undefined}
          role={!step.signed ? 'button' : undefined}
          tabIndex={!step.signed ? 0 : undefined}
          onKeyDown={!step.signed ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollToBlock(step.id); } } : undefined}
        >
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
              step.signed
                ? 'bg-emerald-100 text-emerald-600'
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {step.signed
              ? <CheckCircle className="w-3.5 h-3.5" />
              : <Clock className="w-3.5 h-3.5" />}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-700 truncate">
              {step.label}
              {(step.signerName || step.email) && (
                <span className="font-normal text-gray-400"> — {step.signerName || step.email}</span>
              )}
            </p>
            {step.signed && step.signedAt ? (
              <p className="text-[11px] text-emerald-600">{formatSignedAt(step.signedAt)}</p>
            ) : !step.signed ? (
              <p className="text-[11px] text-gray-400 hover:text-gray-600">Pending</p>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
